/**
 * One report page at its true shape (16:9 slide, A4 document page): it
 * measures its width, derives the row height that keeps the shape, and draws
 * a cover, a contents page or the widget grid. Used by the builder (editable)
 * and by Present mode (read-only, with its own queries).
 */

import { useLayoutEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react';

import { Canvas } from './Canvas.tsx';
import { PAGE_FORMAT } from './grid.ts';
import { useQueries } from './hooks.ts';
import { ContentsPage, CoverPage, ReportPagesContext, type CoverFacts } from './PageArt.tsx';
import { ReportFactsContext, type ReportFacts } from './report-facts.ts';
import { Present } from './Present.tsx';
import type { ReportTheme } from './report-theme.ts';
import type { CompareSetting, DocumentKind, Page, RangeSetting } from './reports-api.ts';
import { ThemeScope } from './ThemeScope.tsx';

/** The width a page is laid out at before Present mode scales it (matches the builder's stage). */
export const DESIGN_WIDTH: Record<string, number> = { SLIDE_DECK: 1100, DOCUMENT: 760 };

type CanvasProps = Omit<ComponentProps<typeof Canvas>, 'maxRows' | 'rowHeight' | 'children'>;

export function PageView({
  kind,
  page,
  pages,
  cover,
  canvas,
  fixedWidth,
}: {
  kind: DocumentKind;
  page: Page;
  pages: Page[];
  cover: CoverFacts;
  canvas: CanvasProps;
  /** Present mode passes the width it computed; the builder lets the page measure itself. */
  fixedWidth?: number | undefined;
}): ReactNode {
  const format = PAGE_FORMAT[kind];
  const ref = useRef<HTMLDivElement | null>(null);
  const [measured, setMeasured] = useState(fixedWidth ?? 960);
  useLayoutEffect(() => {
    if (fixedWidth !== undefined || !ref.current) return undefined;
    const observer = new ResizeObserver(([entry]) => setMeasured(entry?.contentRect.width ?? 960));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [fixedWidth]);

  const facts: ReportFacts = {
    clientName: cover.clientName,
    clientUrl: cover.clientUrl,
    reportName: cover.reportName,
    periodLabel: cover.periodLabel,
    agencyName: cover.preparedBy,
    pageTitle: page.title,
  };
  if (!format) {
    return (
      <ReportFactsContext.Provider value={facts}>
        <ReportPagesContext.Provider value={pages}>
          <Canvas {...canvas} />
        </ReportPagesContext.Provider>
      </ReportFactsContext.Provider>
    );
  }
  const layout = page.settings.layout ?? 'grid';
  // Present mode draws the page at its design width, then scales the whole
  // page to the screen, so text and charts grow with the slide as they do in
  // a real presentation. The builder draws at the width it has, unscaled, so
  // drag and resize keep working in plain pixels.
  const width = fixedWidth === undefined ? measured : (DESIGN_WIDTH[kind] ?? measured);
  const sheet = (
    <div className="rpv-sheet" style={{ height: width * format.ratio }}>
      {layout === 'cover' && <CoverPage facts={cover} />}
      {layout === 'contents' && <ContentsPage pages={pages} />}
      {layout === 'grid' && (
        <ReportFactsContext.Provider value={facts}>
          <ReportPagesContext.Provider value={pages}>
            <Canvas
              {...canvas}
              maxRows={format.rows}
              rowHeight={(width * format.ratio) / format.rows}
            />
          </ReportPagesContext.Provider>
        </ReportFactsContext.Provider>
      )}
    </div>
  );
  if (fixedWidth === undefined) {
    return (
      <div ref={ref} className={`rpv rpv-${kind.toLowerCase()}`}>
        {sheet}
      </div>
    );
  }
  return (
    <div
      className={`rpv rpv-${kind.toLowerCase()}`}
      style={{ width: fixedWidth, height: fixedWidth * format.ratio, overflow: 'hidden' }}
    >
      <div style={{ width, transform: `scale(${fixedWidth / width})`, transformOrigin: '0 0' }}>
        {sheet}
      </div>
    </div>
  );
}

/** A page in Present mode: read-only, querying its own widgets as it comes on screen. */
function PresentedPage({
  organisationId,
  businessUnitId,
  query,
  kind,
  page,
  pages,
  cover,
  sources,
  width,
}: {
  organisationId: string;
  businessUnitId: string | null;
  query: {
    mode: 'LIVE' | 'SAMPLE';
    timezone: string;
    range: RangeSetting;
    compare: CompareSetting;
  };
  kind: DocumentKind;
  page: Page;
  pages: Page[];
  cover: CoverFacts;
  sources: CanvasProps['sources'];
  width: number;
}): ReactNode {
  const keyOf = (index: number) => page.widgets[index]?.id ?? `w${index}`;
  const { results } = useQueries({
    organisationId,
    businessUnitId,
    ...query,
    widgets: page.widgets,
    keyOf,
  });
  return (
    <PageView
      kind={kind}
      page={page}
      pages={pages}
      cover={cover}
      fixedWidth={width}
      canvas={{
        widgets: page.widgets,
        results,
        sources,
        editable: false,
        autoAlign: false,
        selected: null,
        onSelect: () => undefined,
        onChange: () => undefined,
        onRemove: () => undefined,
        keyOf,
      }}
    />
  );
}

type PresentProps = Omit<ComponentProps<typeof PresentedPage>, 'page' | 'width'> & {
  start: number;
  theme?: ReportTheme | Record<string, unknown>;
  onClose: () => void;
};

/** Present mode over a whole report: one page at a time at its true shape. */
export function PresentReport({ start, onClose, theme, ...shared }: PresentProps): ReactNode {
  const format = PAGE_FORMAT[shared.kind];
  if (!format) return null;
  return (
    <Present
      count={shared.pages.length}
      ratio={format.ratio}
      start={start}
      onClose={onClose}
      renderPage={(index, width) => (
        <ThemeScope key={shared.pages[index]?.id ?? index} theme={theme} layout="report">
          <PresentedPage {...shared} page={shared.pages[index] as Page} width={width} />
        </ThemeScope>
      )}
    />
  );
}
